import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ViewChild,
  OnChanges,
  SimpleChanges,
  output,
  input,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import {
  MatPaginator,
  MatPaginatorModule,
  PageEvent,
} from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import {
  MatTable,
  MatTableDataSource,
  MatTableModule,
} from '@angular/material/table';
import { provideNativeDateAdapter } from '@angular/material/core';
import { contractOutput } from '#backend/contract';
import {
  MonitoringAreaTableItem,
  MonitoringAreaTableConfig,
} from './monitoring-areas-table.types';

// Get MonitoringArea type from contract
type MonitoringArea = NonNullable<
  contractOutput['monitoring']['areas']['byId']
>;

@Component({
  selector: 'app-monitoring-areas-table',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatPaginatorModule,
    MatProgressBarModule,
    MatSortModule,
    MatTableModule,
  ],
  templateUrl: './monitoring-areas-table.component.html',
  styleUrl: './monitoring-areas-table.component.scss',
  providers: [provideNativeDateAdapter()],
  encapsulation: ViewEncapsulation.None,
})
export class MonitoringAreasTableComponent implements AfterViewInit, OnChanges {
  dataSource = input(new MatTableDataSource<MonitoringAreaTableItem>([]));
  isLoading = input(false);
  length = input(0);
  config = input<MonitoringAreaTableConfig>({
    showActionsColumn: false,
    columns: [
      'id',
      'uniqueTerritoryFieldValue',
      'carFieldValue',
      'contractFieldValue',
      'insurancePolicyFieldValue',
      'producerIdFieldValue',
      'identityDocumentFieldValue',
      'customerFarmNameValue',
      'areaOperationHa',
      'city',
      'createdBy',
      'createdDate',
    ],
    initialSort: { active: 'createdDate', direction: 'desc' },
  });

  sortChange = output<Sort>();
  pageChange = output<PageEvent>();
  searchChange = output<string>();

  searchControl = new FormControl('');

  get displayedColumns(): string[] {
    return [...this.config().columns];
  }

  getColumnLabel(columnKey: string): string {
    const config = this.config();
    const fieldKeys = config.fieldKeys;

    // Labels dinâmicos baseados nos fieldKeys
    const dynamicLabels: Record<string, string> = {
      uniqueTerritoryFieldValue: fieldKeys?.uniqueTerritoryFieldKey
        ? `Id de área (${fieldKeys.uniqueTerritoryFieldKey})`
        : 'Id de área',
      carFieldValue: fieldKeys?.carFieldKey
        ? `CAR (${fieldKeys.carFieldKey})`
        : 'CAR',
      contractFieldValue: fieldKeys?.contractFieldKey
        ? `Contrato (${fieldKeys.contractFieldKey})`
        : 'Contrato',

      insurancePolicyFieldValue: fieldKeys?.insurancePolicyKey
        ? `Apólice (${fieldKeys.insurancePolicyKey})`
        : 'Apólice de seguro',
      producerIdFieldValue: fieldKeys?.producerIdFieldKey
        ? `Produtor (${fieldKeys.producerIdFieldKey})`
        : 'ID do produtor',
      identityDocumentFieldValue: fieldKeys?.identityDocumentFieldKey
        ? `Documento (${fieldKeys.identityDocumentFieldKey})`
        : 'Documento de identidade',
      customerFarmNameValue: fieldKeys?.customerFarmNameKey
        ? `Nome da fazenda (${fieldKeys.customerFarmNameKey})`
        : 'Nome da fazenda',
    };

    // Labels fixos
    const staticLabels: Record<string, string> = {
      id: '#',
      areaOperationHa: 'Área (ha)',
      city: 'Município',
      state: 'UF',
      createdBy: 'Responsável',
      createdDate: 'Data Hora',
    };

    return dynamicLabels[columnKey] || staticLabels[columnKey] || columnKey;
  }

  @ViewChild(MatSort) declare sort: MatSort;
  @ViewChild(MatPaginator) declare paginator: MatPaginator;
  @ViewChild(MatTable) declare table: MatTable<MonitoringAreaTableItem>;

  ngAfterViewInit() {
    if (this.sort) {
      const config = this.config();
      if (config.initialSort?.active) {
        this.sort.active = config.initialSort.active;
        this.sort.direction = config.initialSort?.direction ?? 'desc';
      }
      this.dataSource().sort = this.sort;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['dataSource'] && this.sort) {
      this.dataSource().sort = this.sort;
    }
  }

  onSortChange(event: Sort) {
    this.sortChange.emit(event);
  }

  onPageChange(event: PageEvent) {
    this.pageChange.emit(event);
  }

  onSearch() {
    const searchTerm = this.searchControl.value || '';
    this.searchChange.emit(searchTerm);
  }

  clearText() {
    this.searchControl.setValue('');
    this.onSearch();
  }
}
